import { useEffect, useMemo, useState } from 'react';

import CommonsService from 'features/commons/CommonsService';

import useErrorCatch from 'hooks/useErrorCatch';
import { useGetResource } from 'hooks/useGetResource';

import { Country } from 'types/commons';

export interface CountryOption {
  code: string;
  label: string;
}

export const useTranslatedCountriesOptions = ({
  url,
  locale,
  isDisableLoad = false
}: {
  url: string;
  locale?: string;
  isDisableLoad?: boolean;
}): { options: CountryOption[]; isLoading: boolean } => {
  const catchError = useErrorCatch();

  const { data: countriesList, isLoading: isCountriesLoading } =
    useGetResource<Country>({
      url,
      isOpenApi: true,
      isDisableLoad
    });

  const [translatedCountries, setTranslatedCountries] = useState<Country[]>([]);
  const [isTranslatedCountriesLoading, setIsTranslatedCountriesLoading] =
    useState(false);

  useEffect(() => {
    const fetchTranslatedCountries = async () => {
      if (isDisableLoad || !locale) {
        setTranslatedCountries([]);
        return;
      }

      try {
        setIsTranslatedCountriesLoading(true);
        const result = await CommonsService.getCountries(locale);
        setTranslatedCountries(result);
      } catch (error) {
        catchError({
          error,
          default: { message: 'Error getting translated countries' }
        });
      } finally {
        setIsTranslatedCountriesLoading(false);
      }
    };

    void fetchTranslatedCountries();
  }, [locale, isDisableLoad]);

  const options = useMemo(() => {
    const translatedByCode = new Map(
      translatedCountries.map((country) => [country.code, country.name])
    );

    return countriesList
      .map(({ code, name }) => ({
        code,
        label: translatedByCode.get(code) ?? name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [countriesList, translatedCountries]);

  return {
    options,
    isLoading: isCountriesLoading || isTranslatedCountriesLoading
  };
};
